import React from 'react';
import cx from 'classnames';

import { FormField, Select, Option } from '@xingternal/text-field';

import styles from './PagesFormField.css';

type ListType = {
  value: string;
  text: string;
}[];

type ValidationResult = null | {
  key: string;
  value?: number;
};

type PagesFormFieldProps = {
  intl: any,
  type: string,
  formState?: any,
  setFormTouched?: (isTouched: boolean) => void,
  setFormState?: (state: any) => void,
  optionList?: ListType,
  defaultValue?: string | number,
  applyToValue?: (value: string) => string,
  applyToValueOnFocus?: (value: string) => string,
  applyToValueOnBlur?: (value: string) => string,
  required?: boolean,
  validate?: (type: string, value: string) => ValidationResult,
  copyPrefix?: string,
  maxAllowedChars?: number,
  className?: string,
  shouldTrim?: boolean,
  multiline?: boolean,
};

const PagesFormField = ({
  intl,
  type,
  formState,
  setFormState,
  setFormTouched,
  optionList,
  defaultValue,
  applyToValue,
  applyToValueOnFocus,
  applyToValueOnBlur,
  required,
  validate,
  shouldTrim,
  copyPrefix,
  maxAllowedChars,
  className,
  ...props
}: PagesFormFieldProps) => {
  const changeHandler = (value: string, blurred?: boolean, focussed?: boolean) => {
    const val =
      applyToValueOnBlur && blurred
        ? applyToValueOnBlur(value)
        : focussed && applyToValueOnFocus
        ? applyToValueOnFocus(value)
        : maxAllowedChars
        ? value.substring(0, maxAllowedChars)
        : value;
    const error = validate && validate(type, shouldTrim ? val.trim() : val);
    const newValue = shouldTrim && blurred ? val.trim() : val;
    setFormState && setFormState({
      ...formState,
      [type]: { value: newValue, showError: !!blurred && !!error, error },
    });
    setFormTouched && setFormTouched(true);
  };
  if (optionList) {
    return (
      <Select
        value={formState?.[type]?.value || defaultValue || ''}
        error={formState?.[type]?.error?.key}
        onChange={(e) => changeHandler && changeHandler(e.target.value)}
        placeholder={intl.formatMessage({
          id: copyPrefix + type.toUpperCase(),
          default: copyPrefix + type.toUpperCase(),
        })}
        medium
        className={cx(styles.field, className)}
        {...props}
      >
        {optionList?.map((item, index) => (
          <Option key={index} value={item.value}>
            {item.text}
          </Option>
        ))}
      </Select>
    );
  }

  return (
    <FormField
      helperText={
        formState?.[type]?.showError && formState?.[type]?.error?.key
          ? intl.formatMessage(
              {
                id: formState[type].error?.key,
                default: formState[type].error?.key,
              },
              { value: formState[type].error?.value }
            )
          : maxAllowedChars
          ? intl.formatMessage(
              {
                id: 'EP_INPUT_CHARS_LEFT',
                default: 'EP_INPUT_CHARS_LEFT',
              },
              {
                charactersNumber:
                  maxAllowedChars - formState?.[type]?.value?.length,
              }
            )
          : !required
          ? intl.formatMessage({
              id: 'EP_OPTIONAL_FIELD_HINT',
              default: 'EP_OPTIONAL_FIELD_HINT',
            })
          : null
      }
      value={
        formState?.[type]?.value
          ? applyToValue
            ? applyToValue(formState?.[type]?.value)
            : formState?.[type]?.value
          : ''
      }
      error={formState?.[type]?.showError}
      onChange={(e: any) => changeHandler(e.target.value)}
      onBlur={(e: any) => changeHandler(e.target.value, true)}
      onFocus={(e: any) =>
        applyToValueOnFocus && changeHandler(e.target.value, false, true)
      }
      label={intl.formatMessage({
        id: copyPrefix + type.toUpperCase(),
        default: copyPrefix + type.toUpperCase(),
      })}
      medium
      className={cx(styles.field, className)}
      {...props}
    />
  );
};

export default PagesFormField;
